import { useState, useCallback, useMemo } from 'react';
import {
    CellAddress,
    CellChange,
    UseTableEditOptions,
    UseTableEditReturn,
    InlineEditConfig,
} from '@/types/table';

export function useTableEdit<TData>(
    options: UseTableEditOptions<TData>,
): UseTableEditReturn<TData> {
    const { getRowId, onSave } = options;

    const [editingCell, setEditingCell] = useState<CellAddress | null>(null);
    const [pendingChanges, setPendingChanges] = useState<
        Map<string, CellChange<TData>>
    >(new Map());
    const [isSaving, setIsSaving] = useState(false);

    const onEditStart = useCallback((cell: CellAddress) => {
        setEditingCell(cell);
    }, []);

    const onEditCommit = useCallback((change: CellChange<TData>) => {
        const key = `${change.rowId}:${change.columnId}`;

        setPendingChanges((prev) => {
            const next = new Map(prev);
            if (change.newValue === change.originalValue) {
                next.delete(key);
            } else {
                next.set(key, change);
            }
            return next;
        });

        setEditingCell(null);
    }, []);

    const onEditCancel = useCallback(() => {
        setEditingCell(null);
    }, []);

    const config: InlineEditConfig<TData> = useMemo(
        () => ({
            getRowId,
            editingCell,
            pendingChanges,
            onEditStart,
            onEditCommit,
            onEditCancel,
        }),
        [
            getRowId,
            editingCell,
            pendingChanges,
            onEditStart,
            onEditCommit,
            onEditCancel,
        ],
    );

    const changes = useMemo(
        () => Array.from(pendingChanges.values()),
        [pendingChanges],
    );

    const save = useCallback(async () => {
        if (pendingChanges.size === 0 || !onSave) return;

        setIsSaving(true);
        try {
            await onSave(Array.from(pendingChanges.values()));
            setPendingChanges(new Map());
        } catch (err) {
            throw err;
        } finally {
            setIsSaving(false);
        }
    }, [pendingChanges, onSave]);

    const discard = useCallback(() => {
        setPendingChanges(new Map());
        setEditingCell(null);
    }, []);

    return {
        config,
        isDirty: pendingChanges.size > 0,
        isSaving,
        save,
        discard,
        changes,
    };
}
